<template>
  <v-row justify="center">
    <v-col xl="10">
      <v-card>
        <v-card-title>
          <h2>{{ $t("admin.activities.list.title") }}</h2>
          <v-btn :icon="$vuetify.breakpoint.smAndDown" class="ml-5" color="primary" text @click="create()">
            <v-icon left>mdi-eye-plus-outline</v-icon>
            <span class="hidden-sm-and-down">{{ $t("admin.activities.actions.add") }}</span>
          </v-btn>
          <v-card> </v-card>
          <v-spacer></v-spacer>
          <v-text-field
            :label="$t('admin.activities.actions.search')"
            append-icon="mdi-magnify"
            hide-details
            single-line
            v-model="search"
          ></v-text-field>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :loading="loading"
          :no-data-text="$t('admin.activities.list.noData')"
          item-key="id"
          class="elevation-1"
        >
          <template v-slot:item.date="props">
            {{ props.item.date | datetime }}
          </template>
          <template v-slot:item.duration="props">
            {{ props.item.duration | duration }}
          </template>

          <template v-slot:item.slug="props">
            <a v-bind:href="buildLink(props.item.slug)">{{ $t("admin.activities.list.landingPage") }}</a>
            <v-btn class="mx-2" dark x-small icon raised color="primary" @click="copy(buildLink(props.item.slug))">
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
          <template v-slot:item.type="props">
            <v-tooltip v-if="props.item.type == 'WORKSHOP'" left>
              <template v-slot:activator="{ on }">
                <v-icon color="primary" v-on="on">mdi-briefcase</v-icon>
              </template>
              <span>{{ $t("admin.activities.list.workshop") }}</span>
            </v-tooltip>
            <v-tooltip v-else-if="props.item.type == 'EXTERNAL'" left>
              <template v-slot:activator="{ on }">
                <v-icon color="primary" v-on="on">mdi-open-in-new</v-icon>
              </template>
              <span>{{ $t("admin.activities.list.external") }}</span>
            </v-tooltip>
            <v-tooltip v-else-if="props.item.type == 'LIVE_STREAM'" left>
              <template v-slot:activator="{ on }">
                <v-icon color="primary" v-on="on">mdi-earth</v-icon>
              </template>
              <span>{{ $t("admin.activities.list.liveStream") }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.controls="props">
            <template v-if="$store.state.event.enableLobby">
              <v-tooltip v-if="props.item.published" left>
                <template v-slot:activator="{ on }">
                  <v-icon color="green" v-on="on" v-on:click="setPublishedState(props.item, false)"
                    >mdi-eye-outline</v-icon
                  >
                </template>
                <span>{{ $t("admin.activities.list.unpublish") }}</span>
              </v-tooltip>
              <v-tooltip v-if="!props.item.published" left>
                <template v-slot:activator="{ on }">
                  <v-icon color="red" v-on="on" v-on:click="setPublishedState(props.item, true)"
                    >mdi-eye-off-outline</v-icon
                  >
                </template>
                <span>{{ $t("admin.activities.list.publish") }}</span>
              </v-tooltip>
            </template>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn class="mx-2" fab dark x-small text raised color="primary" v-on="on" @click="edit(props.item)">
                  <v-icon dark>mdi-pencil</v-icon>
                </v-btn>
                <input type="hidden" id="link-copy" />
              </template>
              <span>{{ $t("admin.activities.list.editTooltip") }}</span>
            </v-tooltip>
            <v-tooltip v-if="props.item.type == 'LIVE_STREAM'" left>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="editChat(props.item)"
                  class="mx-2"
                  color="primary"
                  dark
                  fab
                  raised
                  text
                  v-on="on"
                  x-small
                >
                  <v-icon dark>mdi-chat-alert</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("admin.activities.list.moderateChatTooltip") }}</span>
            </v-tooltip>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="participantsList(props.item)"
                  class="mx-2"
                  color="primary"
                  dark
                  fab
                  raised
                  text
                  v-on="on"
                  x-small
                >
                  <v-icon dark>mdi-account-multiple</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("admin.activities.list.participants") }}</span>
            </v-tooltip>
            <v-tooltip left v-if="props.item.enableDiploma">
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="diplomaCustomization(props.item)"
                  class="mx-2"
                  color="primary"
                  dark
                  fab
                  raised
                  text
                  v-on="on"
                  x-small
                >
                  <v-icon dark>mdi-certificate-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("admin.activities.list.customizeDiploma") }}</span>
            </v-tooltip>
            <ActivitiesEditWallModal
              v-if="showParticipantWallIcon(props.item)"
              :activity-id="props.item.id"
              :internal="false"
            />
            <v-tooltip v-if="props.item.schedule && props.item.schedule.length && props.item.date" left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  class="mx-2"
                  color="primary"
                  dark
                  fab
                  raised
                  text
                  x-small
                  @click="editSchedule(props.item)"
                >
                  <v-icon dark>mdi-clock-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("admin.activities.list.editSchedule") }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <ActivitiesEditChatModal ref="activitiesEditChatModal" />
    <ActivitiesEditParticipantsListModal ref="activitiesEditParticipantsListModal" />
    <ActivitiesEditScheduleModal ref="activitiesEditScheduleModal" />
  </v-row>
</template>
<script>
import debounce from "../../../util/debounce";
import ActivitiesEditChatModal from "./activities.edit.chatModal";
import ActivitiesEditParticipantsListModal from "./activities.edit.participantsListModal";
import ActivitiesEditWallModal from "./activities.edit.wallModal";
import ActivitiesEditScheduleModal from "./activities.edit.scheduleModal";

export default {
  components: {
    ActivitiesEditScheduleModal,
    ActivitiesEditWallModal,
    ActivitiesEditChatModal,
    ActivitiesEditParticipantsListModal,
  },
  data() {
    return {
      valid: false,
      totalItems: 0,
      items: [],
      loading: true,
      search: "",
      pagination: {
        itemsPerPage: 25,
      },
      headers: [
        { text: this.$t("admin.activities.list.name"), value: "name" },
        { text: this.$t("admin.activities.list.date"), value: "date" },
        { text: this.$t("admin.activities.list.duration"), value: "duration" },
        {
          text: this.$t("admin.activities.list.link"),
          value: "slug",
          sortable: false,
        },
        {
          text: this.$t("admin.activities.list.type"),
          value: "type",
          width: "80px",
        },
        {
          text: this.$t("admin.activities.list.actions"),
          value: "controls",
          sortable: false,
        },
      ],
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
      this.loading = true;
      const result = await this.$axios.get(`/event/${this.$route.params.eventId}/activities`, {
        params: {
          page: page - 1,
          size: itemsPerPage,
          sortBy: sortBy.join(","),
          sortOrder: sortDesc.map((s) => (s ? "desc" : "asc")).join(","),
          search: this.search || undefined,
        },
      });
      this.items = result.rows;
      this.totalItems = result.count;
      this.loading = false;
    },
    edit(item) {
      this.$router.push({ name: `activitiesEdit`, params: { id: item.id } });
    },
    diplomaCustomization(item) {
      this.$router.push({ name: `activitiesDiplomaCustomization`, params: { id: item.id } });
    },
    editChat(item) {
      this.$refs.activitiesEditChatModal.open(item);
    },
    participantsList(activity) {
      this.$refs.activitiesEditParticipantsListModal.open(activity);
    },
    editSchedule(activity) {
      this.$refs.activitiesEditScheduleModal.open(activity);
    },
    create() {
      this.$router.push({ name: `activitiesCreate` });
    },
    copy(link) {
      let linkCopySource = document.querySelector("#link-copy");
      linkCopySource.setAttribute("value", link);
      linkCopySource.setAttribute("type", "text");
      linkCopySource.select();
      document.execCommand("selectAll");
      if (document.execCommand("copy")) {
        this.$store.commit("SUCCESS", this.$t("admin.activities.list.copied"));
      }
      linkCopySource.setAttribute("type", "hidden");
      window.getSelection().removeAllRanges();
    },
    buildLink(slug) {
      return `${window.location.origin}/online/${slug}`;
    },
    async setPublishedState(item, state) {
      item.published = state;
      await this.$axios.put(`/event//activities/session/${item.id}/publishStatus`, item);
    },
    showParticipantWallIcon(activity) {
      if (!activity.schedule || activity.schedule.length == 0) {
        return false;
      }
      return activity.schedule.find((s) => s.enableTakeASelfie == true) ? true : false;
    },
  },
  watch: {
    pagination: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
    search: {
      handler() {
        debounce(this.loadData);
      },
      deep: true,
    },
  },
};
</script>
